<template>
  <div class="relative">
    <BrandInput
      v-bind="$attrs"
      type="text"
      :name="formname"
      :id="`Autocomplete${formname || ''}`"
      :placeholder="placeholder"
      autocomplete="off"
      class="autocomplete__input"
      :class="inputclass"
      :value="data.query"
      v-model="data.query"
      @blur="data.showlist = false"
      @focus="openlist()"
      @keydown.up="move(data.activeitem - 1)"
      @keydown.down="move(data.activeitem + 1)"
      @keydown.enter="selectitem($event)"
      ref="inputElement"
      :label="data.label"
      :isDisabled="isDisabled"
    />
    <i
      v-if="data.loading"
      class="fas fa-circle-notch fa-spin absolute right-0 top-0 m-3 mt-10 pt-1 pb-1"
    ></i>
    <input v-if="isplaces" type="hidden" v-model="data.lat" :name="`${formname || ''}Lat`" />
    <input v-if="isplaces" type="hidden" v-model="data.lng" :name="`${formname || ''}Lng`" />
    <p v-if="data.errorMessage" class="font14 mt-2">{{ data.errorMessage }}</p>
    <ul class="autocomplete__list shadow-xl bg-white" v-if="data.showlist" ref="listElement">
      <li v-if="data.noResults" class="autocomplete__list__item bold">
        No results for '{{ data.query }}'
      </li>
      <li
        class="autocomplete__list__item cursor-pointer"
        v-bind:key="index"
        v-for="(location, index) in data.list"
        :class="index === data.activeitem ? 'active' : ''"
        @mouseenter="data.activeitem = index"
        @mousedown="selectitem($event)"
        :data-autocomplete-location="location.description"
      >
        {{ location.description }}
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import debounce from 'lodash.debounce';
import { defineComponent, reactive, ref, watch } from 'vue';
import { uuidv4 } from '../../helpers/functions';
import BrandInput from '../BrandInput/BrandInput.vue';

interface AutoCompleteLocation {
  name: string;
  lat: number;
  lng: number;
}

type AutoCompleteData = {
  label: string;
  query: string;
  list: Array<any>;
  loading: boolean;
  showlist: boolean;
  validLocation: boolean;
  noResults: boolean;
  activeitem: number;
  name: string;
  errorMessage?: string;
  lat: number;
  lng: number;
  sessionToken?: string;
};

export default defineComponent({
  name: 'AutoComplete',
  inheritAttrs: false,

  components: {
    BrandInput,
  },

  props: {
    placeholder: String, // Input placeholder
    formname: String, // Form name attribute, also used as an identifier for Vuemit events
    inputclass: String, // Input element class names
    isplaces: Boolean, // Flag for if this autocomplete should run a places request for lat/lng
    label: String,
    initLocation: Object as () => AutoCompleteLocation,
    api: Object,
    isDisabled: Boolean,
  },

  setup(props, { emit }) {
    const listElement = ref<HTMLUListElement | null>(null);
    const data = reactive<AutoCompleteData>({
      label: props.label ? props.label : '',
      query: props.initLocation?.name || '',
      list: [],
      loading: false,
      showlist: false,
      validLocation: props.initLocation !== null,
      noResults: false,
      activeitem: 0,
      name: props.initLocation?.name || '',
      lat: props.initLocation?.lat || 0,
      lng: props.initLocation?.lng || 0,
    });

    data.sessionToken = uuidv4(); // Generate session token for gmap api

    //methods
    const emitToParent = () => {
      if (!data.name || !data.lat || !data.lng) return;
      emit('autocompleted', {
        Address: data.name,
        Latitude: data.lat,
        Longitude: data.lng,
      });
    };

    const geocode = () => {
      emit('geocode', data.query);
      if (data.query.length < 3) return;

      data.loading = true;
      data.validLocation = false;

      const model = {
        location: data.query,
        sessionToken: data.sessionToken,
      };

      props.api
        ?.AutocompleteProxy(model)
        .then((resp: any) => {
          const { predictions, status } = resp;

          data.noResults = status === 'ZERO_RESULTS';
          data.showlist = true;
          data.list = predictions;
          data.loading = false;
        })
        .catch((_error: any) => {
          data.loading = false;
        });
    };

    const move = (newindex: number) => {
      data.activeitem = Math.min(Math.max(newindex, 0), data.list.length - 1);
      if (!listElement.value) return;
      const { children, offsetHeight, scrollTop } = listElement.value;
      const newItem: HTMLElement = children[data.activeitem] as HTMLElement;

      // If the newly selected element is above the top of the scroll window, or below the bottom edge
      if (
        newItem &&
        (newItem.offsetTop < scrollTop || newItem.offsetTop > scrollTop + offsetHeight)
      ) {
        (listElement.value as HTMLUListElement).scrollTop = newItem.offsetTop;
      }
    };

    const openlist = () => {
      if (data.list.length) {
        data.showlist = true;
      }
    };

    const places = (placeId: any) => {
      data.loading = true;
      props.api
        ?.GeocodeProxy({ placeId })
        .then((resp: any) => {
          if (!resp) return;
          data.loading = false;
          const place = resp.results[0];
          data.lat = place.geometry.location.lat;
          data.lng = place.geometry.location.lng;

          data.name = place.formatted_address;
          data.query = place.formatted_address;
          emitToParent();
        })
        .catch(() => {
          data.loading = false;
        });
    };

    const selectitem = ($event: Event) => {
      if (!data.showlist) return;

      $event.preventDefault();
      const selected = data.list[data.activeitem];
      data.showlist = false;
      data.validLocation = true;
      data.errorMessage = undefined;

      // If it's a suggested location with coordinates, just emit to parent
      if (selected.latitude && selected.longitude) {
        data.query = selected.description;
        data.name = selected.description;
        emit('autocompleted', {
          Address: selected.description,
          Latitude: selected.latitude,
          Longitude: selected.longitude,
        });
        return;
      }

      if (props.isplaces) {
        // Run places request too
        places(selected.place_id);
      } else {
        // Simple geocode is fine
        data.query = selected.description;
        data.name = selected.description;
        emit('autocompleted', data.name);
      }
    };

    const setLocation = (address: string, latitude: number, longitude: number): void => {
      if (!address || !latitude || !longitude) return;

      data.lat = latitude;
      data.lng = longitude;
      data.query = address;
      data.name = address;
      data.validLocation = true;
      emitToParent();
    };

    const clearLocation = () => {
      data.lat = 0;
      data.lng = 0;
      data.query = '';
      data.name = '';
      data.validLocation = false;

      emit('autocompleted', null);
    };

    const updateLocation = (newLocation: any) => {
      data.lat = newLocation.Latitude;
      data.lng = newLocation.Longitude;
      data.query = newLocation.Address;
      data.name = newLocation.Address;
      data.validLocation = true;
    };

    const debouncedGeocode = debounce(geocode, 500);

    //watchers
    watch(
      () => data.query,
      () => {
        if (data.noResults) data.noResults = false;
        if (data.query === '') clearLocation();
        if (data.query !== data.name) debouncedGeocode();
      },
    );

    return {
      data,
      placeholder: props.placeholder,
      formname: props.formname,
      inputclass: props.inputclass,
      isplaces: props.isplaces,
      openlist,
      move,
      selectitem,
      listElement,
      clearLocation,
    };
  },
});
</script>

<style lang="scss" scoped>
.autocomplete {
  display: inline-block;
  position: relative;
  text-align: left;

  &--block {
    width: 100%;
  }
}

.autocomplete__iconoffset {
  padding-right: 2.5rem;
}

.autocomplete__list {
  position: absolute;
  top: 110%;
  left: 0;
  width: 100%;
  z-index: 6;
  max-height: 276.5px;
  overflow-y: auto;
}

.autocomplete__list__item {
  border-bottom: 0.5px solid #ccc;
  padding: 10px 16px;
  font-size: 14px;

  &--suggested {
    padding-right: 6rem;
  }

  &:last-child {
    border: none;
  }

  &.active {
    @apply bg-sec text-white;
  }
}

.autocomplete__tag {
  position: absolute;
  top: 0;
  right: 1rem;
  bottom: 0;
  height: 19px;
  margin: auto;
}

//@include breakpoint($lg-min) {

.autocomplete__originoffset {
  border-right: none;
  padding-right: 2rem;

  & ~ .input__icon {
    right: 2rem; // Move icon when we have a switcheroo in the middle
  }
}

.autocomplete__destoffset {
  border-left: none;
  padding-left: 2rem; // Move text when we have a switcheroo
}

//}
</style>
